import {FC, Key, useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {Badge} from "@interstate/components/Badge";
import {MagnifyingGlassIcon} from "@interstate/components/Icons";
import {Table} from "@interstate/components/Table";
import {TextInput} from "@interstate/components/TextInput";
import {PendoEvent} from "@common-core/react-pendo/model";
import {editTableBadgeMetaData} from "./editTableBadgeMetaData";
import {useNavigation} from "../../../navigation";
import {TableSorterState} from "../../../pendo/events";
import {EntitySourceType} from "../../EntitySourceType";
import "./BoidAssignmentTable.scss";
import {
  BoidAssignmentState,
  BoidAssignmentStatus,
  BoidAssignmentTableData
} from "../model";
import {
  ASSIGN_BOID_ENTITY,
  UNASSIGN_BOID_ENTITY,
  useAuthorization
} from "../../../access";
import {buildSearch, SearchFunction} from "../search";
import {toBoidAssignmentTableData} from "../transforms";
import {OnChangeSorterType} from "@interstate/components/Table/Types/tableTypes";

// Type for boid assignment callbacks that can optionally include event data
type BoidAssignmentCallback<E = any> = (
  boid: BoidAssignmentState,
  event?: E
) => void;

export interface BoidAssignmentTableProps {
  boids: BoidAssignmentState[];
  entityId: string;
  onAssignBoid: BoidAssignmentCallback;
  onUnassignBoid: BoidAssignmentCallback;
  working?: boolean;
  filterText: string;
  onFilterTextChange: (value: string) => void;
}

/**
 * BoidAssignmentTable component renders a table of Boids with their status and selectable rows.
 * Assigned BOIDs are pre-selected, and selection changes trigger assignment/unassignment operations.
 */
export const BoidAssignmentTable: FC<BoidAssignmentTableProps> = ({
  boids = [],
  entityId,
  onAssignBoid,
  onUnassignBoid,
  working = false,
  filterText,
  onFilterTextChange
}) => {
  const {t} = useTranslation();
  const {navigateToEntity} = useNavigation();

  const allTableData = useMemo<BoidAssignmentTableData[]>(
    () => (boids ?? []).map(b => toBoidAssignmentTableData(b, t, entityId)),
    [boids, t, entityId]
  );

  const search = useMemo<SearchFunction>(
    () => buildSearch(allTableData),
    [allTableData]
  );

  const tableData = useMemo<BoidAssignmentTableData[]>(() => {
    if (!filterText || filterText.trim().length === 0) {
      return allTableData;
    }
    return search(filterText);
  }, [allTableData, filterText, search]);

  const granted = useAuthorization();
  const canAssignBoid = useMemo<boolean>(
    () => granted([ASSIGN_BOID_ENTITY]),
    [granted]
  );
  const canUnassignBoid = useMemo<boolean>(
    () => granted([UNASSIGN_BOID_ENTITY]),
    [granted]
  );

  // Helper function to determine which BOIDs should be disabled in the table
  const getDisabledBoidKeys = (): (string | number)[] => {
    if (working) {
      return tableData.map(boid => boid.key);
    }
    return tableData
      .filter(boid => working || boid.operationalBoidStatus === "unavailable")
      .map(boid => boid.key);
  };

  // Calculate initially checkmarked rows (assigned or orphaned BOIDs)
  const checkmarkedRowKeys = useMemo<Key[]>(() => {
    return tableData
      .filter(
        boid =>
          boid.operationalBoidStatus === "assigned" ||
          boid.operationalBoidStatus === "orphaned"
      )
      .map(boid => boid.key);
  }, [tableData]);

  // Handle row/checkbox selection changes
  const onCheckboxChange = useCallback(
    (selectedKeys: Key[]) => {
      const newSelectedKeys = new Set(selectedKeys);
      const currentlyAssignedKeys = new Set(checkmarkedRowKeys);

      const keysToAssign = [...newSelectedKeys].filter(
        key => !currentlyAssignedKeys.has(key)
      );
      const keysToUnassign = [...currentlyAssignedKeys].filter(
        key => !newSelectedKeys.has(key)
      );

      const findBoidByKey = (key: Key) => tableData.find(b => b.key === key);

      // Filter function for BOIDs that can be assigned
      const availableBoids = (
        boid: BoidAssignmentTableData | undefined
      ): boid is BoidAssignmentTableData => {
        return !!(boid && boid.operationalBoidStatus === "available");
      };

      // Filter function for BOIDs that can be unassigned
      const assignedOrOrphaned = (
        boid: BoidAssignmentTableData | undefined
      ): boid is BoidAssignmentTableData => {
        return !!(
          boid &&
          (boid.operationalBoidStatus === "assigned" ||
            boid.operationalBoidStatus === "orphaned")
        );
      };

      if (canAssignBoid) {
        keysToAssign
          .map(findBoidByKey)
          .filter(availableBoids)
          .forEach(boid => {
            onAssignBoid(boid.boidAssignmentState);
          });
      }

      if (canUnassignBoid) {
        keysToUnassign
          .map(findBoidByKey)
          .filter(assignedOrOrphaned)
          .forEach(boid => {
            onUnassignBoid(boid.boidAssignmentState);
          });
      }
    },
    [
      checkmarkedRowKeys,
      tableData,
      onAssignBoid,
      onUnassignBoid,
      canAssignBoid,
      canUnassignBoid
    ]
  );

  const renderOperationalStatusBadge = useCallback(
    (status: BoidAssignmentStatus, boid: BoidAssignmentTableData) => {
      const badgeMetadata = editTableBadgeMetaData(status);

      const badge = (
        <Badge
          variant={badgeMetadata.variant}
          className={`boid-status-badge boid-status-badge--${status}`}
          endIcon={badgeMetadata.endIcon}>
          {boid.operationalBoidStatusLabel}
        </Badge>
      );

      // Make unavailable badges clickable if they have an assigned entity
      if (status === "unavailable" && boid.boidAssignmentState.assignedTo) {
        return (
          <button
            type="button"
            onClick={() =>
              navigateToEntity(
                boid.boidAssignmentState.assignedTo!,
                EntitySourceType.OPERATIONAL
              )
            }
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer"
            }}
            aria-label={`Navigate to entity ${boid.boidAssignmentState.assignedTo}`}>
            {badge}
          </button>
        );
      }

      return badge;
    },
    [navigateToEntity]
  );

  const trackSorterChange: OnChangeSorterType = useCallback(sorter => {
    // Normalize to an array to handle both single and multiple sorters uniformly
    (Array.isArray(sorter) ? sorter : [sorter])
      .map<PendoEvent<TableSorterState>>(result => {
        // When the column object is present, it indicates a sorter has been applied;
        // absence indicates the sorter was removed
        return result.column
          ? {
              name: "BOID Assignment Table Sorter Applied",
              metadata: {
                columnName: String(result.column.title),
                sortOrder:
                  result.order === "descend" ? "Descending" : "Ascending"
              }
            }
          : {
              name: "BOID Assignment Table Sorter Removed"
            };
      })
      .forEach(({name, metadata}) => {
        console.dir({name, metadata});
        window.pendo?.track(name, metadata);
      });
  }, []);

  if (tableData.length === 0) {
    return (
      <div className={"boid-assignment-table-container"}>
        <TextInput
          id={"boid-assignment-filter"}
          data-testid={"boid-assignment-filter"}
          name={"boid-assignment-filter"}
          label={t("entity-detail.boid-edit-table.filter")}
          value={filterText}
          onChange={e => onFilterTextChange(e.target.value)}
          inputPrefix={<MagnifyingGlassIcon />}
          clearButton={true}
        />
        <div className="no-boids-message" data-testid="no-boids-message">
          {t("entity-detail.boid-edit-table.no-boids")}
        </div>
      </div>
    );
  }

  return (
    <div className={"boid-assignment-table-container"}>
      <TextInput
        id={"boid-assignment-filter"}
        data-testid={"boid-assignment-filter"}
        name={"boid-assignment-filter"}
        label={t("entity-detail.boid-edit-table.filter")}
        value={filterText}
        onChange={e => onFilterTextChange(e.target.value)}
        inputPrefix={<MagnifyingGlassIcon />}
        clearButton={true}
      />
      <Table
        id="boid-edit-table"
        data-testid="boid-edit-table"
        data={tableData}
        defaultPageSize={15}
        enableRowSelection={canAssignBoid || canUnassignBoid}
        selectedRowKeys={checkmarkedRowKeys}
        onChangeSelectedRows={onCheckboxChange}
        disabledSelectedRowKeys={getDisabledBoidKeys()}
        onChangeSorter={trackSorterChange}
        sortableColumns={true}
        columns={[
          {
            className: "column-product",
            dataIndex: "product",
            title: t("entity-detail.boid-edit-table.product"),
            columnSortable: true,
            defaultSortOrder: "ascend",
            sorter: (a: BoidAssignmentTableData, b: BoidAssignmentTableData) =>
              a.product.localeCompare(b.product)
          },
          {
            className: "column-id",
            dataIndex: "id",
            title: t("entity-detail.boid-edit-table.id"),
            columnSortable: true,
            sorter: (a: BoidAssignmentTableData, b: BoidAssignmentTableData) =>
              a.id.localeCompare(b.id)
          },
          {
            className: "column-operational-status",
            dataIndex: "operationalBoidStatus",
            title: t("entity-detail.boid-edit-table.operational-boid-status"),
            columnSortable: true,
            sorter: (a: BoidAssignmentTableData, b: BoidAssignmentTableData) =>
              a.operationalBoidStatus.localeCompare(b.operationalBoidStatus),
            render: (status: BoidAssignmentStatus, boid: BoidAssignmentTableData) =>
              renderOperationalStatusBadge(status, boid)
          },
          {
            className: "column-client-boid-status",
            dataIndex: "clientBoidStatus",
            title: t(
              "entity-detail.boid-edit-table.client-boid-status",
              "Client BOID Status"
            ),
            columnSortable: true,
            sorter: (a: BoidAssignmentTableData, b: BoidAssignmentTableData) => {
              const aStatus = a.clientBoidStatus || "";
              const bStatus = b.clientBoidStatus || "";
              return aStatus.localeCompare(bStatus);
            }
          }
        ]}
      />
    </div>
  );
};
